import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Check, ArrowRight, ArrowLeft, AlertCircle, KeyRound, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import api, { mensajeDeError } from '../lib/api';
import { toast } from 'sonner';

/**
 * Registro de profesionales.
 *
 * Esta página vive en un enlace privado: no está en el menú, ni en el pie, ni
 * en el mapa del sitio, y lleva noindex. El equipo la envía a mano a quien
 * quiere sumar.
 *
 * Registrarse NO da acceso: crea una postulación que el administrador revisa
 * antes de dejar a nadie atender pacientes.
 */

const PASOS = [
  { id: 1, nombre: 'Tus datos', descripcion: 'Quién eres y cómo contactarte' },
  { id: 2, nombre: 'Tu práctica', descripcion: 'Especialidades y experiencia' },
  { id: 3, nombre: 'Tu cuenta', descripcion: 'Clave y confirmación' },
];

const RegistroProfesionalPage = () => {
  const [estado, setEstado] = useState(null);
  const [paso, setPaso] = useState(1);
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);
  const [verClave, setVerClave] = useState(false);

  const [datos, setDatos] = useState({
    name: '',
    email: '',
    phone: '',
    registration_number: '',
    specialties: '',
    languages: 'Español',
    education: '',
    years_experience: '',
    bio: '',
    price_from: 30000,
    profile_image: '',
    password: '',
    access_code: '',
  });

  const [aceptaCondiciones, setAceptaCondiciones] = useState(false);

  useEffect(() => {
    // La página se bloquea a los buscadores aunque nadie la enlace.
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);

    api
      .get('/registro/profesional')
      .then(({ data }) => setEstado(data))
      .catch(() => setEstado({ open: false, requires_code: false }));

    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const cambiar = (campo, valor) => setDatos((a) => ({ ...a, [campo]: valor }));

  const siguiente = () => {
    if (paso === 1) {
      if (datos.name.trim().length < 3) return toast.error('Escribe tu nombre completo');
      if (!/^\S+@\S+\.\S+$/.test(datos.email)) return toast.error('Revisa tu correo');
      if (!datos.registration_number.trim()) {
        return toast.error('El número de registro es obligatorio');
      }
    }

    if (paso === 2 && !datos.specialties.trim()) {
      return toast.error('Indica al menos una especialidad');
    }

    setPaso((p) => Math.min(3, p + 1));
  };

  const enviar = async (evento) => {
    evento.preventDefault();

    if (datos.password.length < 8) {
      return toast.error('La contraseña debe tener al menos 8 caracteres');
    }
    if (!aceptaCondiciones) {
      return toast.error('Debes aceptar las condiciones para postular');
    }
    if (estado?.requires_code && !datos.access_code.trim()) {
      return toast.error('Ingresa el código de invitación que te enviamos');
    }

    setEnviando(true);
    try {
      await api.post('/registro/profesional', {
        ...datos,
        specialties: datos.specialties.split(',').map((s) => s.trim()).filter(Boolean),
        languages: datos.languages.split(',').map((s) => s.trim()).filter(Boolean),
        education: datos.education.split(',').map((s) => s.trim()).filter(Boolean),
        price_from: Number(datos.price_from) || 30000,
        years_experience: Number(datos.years_experience) || 0,
      });

      setListo(true);
    } catch (error) {
      toast.error(mensajeDeError(error, 'No pudimos enviar tu postulación'));
    } finally {
      setEnviando(false);
    }
  };

  if (estado && !estado.open) {
    return (
      <Marco>
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-5">
            <AlertCircle className="w-7 h-7 text-slate-500" />
          </div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-3">
            El registro está cerrado por ahora
          </h1>
          <p className="text-slate-600">
            No estamos sumando profesionales en este momento. Escríbenos a
            contacto@anonimamente.cl si quieres que te avisemos cuando reabramos.
          </p>
        </div>
      </Marco>
    );
  }

  if (listo) {
    return (
      <Marco>
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-teal-700" />
          </div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-3">
            Recibimos tu postulación
          </h1>
          <p className="text-slate-600 mb-6">
            Vamos a verificar tu número de registro ante la Superintendencia de Salud y te
            escribiremos a <strong>{datos.email}</strong> con la respuesta.
          </p>
          <div className="p-4 rounded-xl bg-slate-50 text-sm text-slate-600 text-left">
            Cuando te aprobemos, entrarás a tu panel con este mismo correo y la contraseña
            que acabas de elegir. Lo primero que tendrás que hacer es configurar tu horario
            semanal: mientras no lo hagas, no aparecerás con horas disponibles.
          </div>
        </div>
      </Marco>
    );
  }

  return (
    <Marco>
      <div className="text-center mb-8">
        <div className="w-12 h-12 rounded-full bg-teal-600 flex items-center justify-center mx-auto mb-4">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <h1 className="font-heading text-3xl font-semibold text-slate-900">
          Postula para atender en AnonimaMente
        </h1>
        <p className="text-slate-600 mt-2 max-w-lg mx-auto">
          Atención psicológica online donde el paciente nunca entrega su nombre. Revisamos
          cada postulación antes de aprobarla.
        </p>
      </div>

      {/* Pasos */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {PASOS.map((p, i) => (
          <React.Fragment key={p.id}>
            <div className="flex flex-col items-center">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium ${
                  paso >= p.id ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-700'
                }`}
              >
                {paso > p.id ? <Check className="w-4 h-4" /> : p.id}
              </div>
              <span className="text-xs text-slate-500 mt-1 hidden sm:block">{p.nombre}</span>
            </div>
            {i < PASOS.length - 1 && (
              <div className={`w-10 h-0.5 ${paso > p.id ? 'bg-teal-600' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <form onSubmit={enviar}>
            {paso === 1 && (
              <div className="space-y-4">
                <Campo
                  id="name"
                  etiqueta="Nombre completo *"
                  valor={datos.name}
                  onChange={(v) => cambiar('name', v)}
                  placeholder="Dra. Ana Pérez Soto"
                />
                <Campo
                  id="email"
                  tipo="email"
                  etiqueta="Correo *"
                  valor={datos.email}
                  onChange={(v) => cambiar('email', v)}
                  placeholder="ana@ejemplo.cl"
                  ayuda="Con este correo entrarás a tu panel y recibirás los avisos de sesiones."
                />
                <Campo
                  id="phone"
                  etiqueta="Teléfono"
                  valor={datos.phone}
                  onChange={(v) => cambiar('phone', v)}
                  placeholder="+56 9 1234 5678"
                  ayuda="Solo para contactarte por temas administrativos. No se muestra a los pacientes."
                />
                <Campo
                  id="registration_number"
                  etiqueta="Número de registro (Superintendencia de Salud) *"
                  valor={datos.registration_number}
                  onChange={(v) => cambiar('registration_number', v)}
                  placeholder="123456"
                  ayuda="Lo verificamos antes de aprobar tu cuenta."
                />
              </div>
            )}

            {paso === 2 && (
              <div className="space-y-4">
                <Campo
                  id="specialties"
                  etiqueta="Especialidades *"
                  valor={datos.specialties}
                  onChange={(v) => cambiar('specialties', v)}
                  placeholder="Ansiedad, Depresión, Duelo"
                  ayuda="Separadas por coma. Es lo que usan los pacientes para encontrarte."
                />
                <Campo
                  id="languages"
                  etiqueta="Idiomas"
                  valor={datos.languages}
                  onChange={(v) => cambiar('languages', v)}
                  placeholder="Español, Inglés"
                />
                <Campo
                  id="education"
                  etiqueta="Formación"
                  valor={datos.education}
                  onChange={(v) => cambiar('education', v)}
                  placeholder="Psicología Universidad de Chile, Magíster en Clínica"
                  ayuda="Separada por coma."
                />

                <div className="grid sm:grid-cols-2 gap-4">
                  <Campo
                    id="years_experience"
                    tipo="number"
                    etiqueta="Años de experiencia"
                    valor={datos.years_experience}
                    onChange={(v) => cambiar('years_experience', v)}
                    placeholder="8"
                  />
                  <Campo
                    id="price_from"
                    tipo="number"
                    etiqueta="Valor por sesión (CLP)"
                    valor={datos.price_from}
                    onChange={(v) => cambiar('price_from', v)}
                    placeholder="30000"
                  />
                </div>

                <div>
                  <Label htmlFor="bio">Presentación</Label>
                  <Textarea
                    id="bio"
                    rows={4}
                    value={datos.bio}
                    onChange={(e) => cambiar('bio', e.target.value)}
                    placeholder="Cuéntale a quien te lea cómo trabajas y con qué te sientes cómodo acompañando."
                    className="mt-1"
                  />
                </div>

                <Campo
                  id="profile_image"
                  etiqueta="Enlace a tu foto"
                  valor={datos.profile_image}
                  onChange={(v) => cambiar('profile_image', v)}
                  placeholder="https://…"
                  ayuda="Opcional. Si no pones una, usamos una imagen genérica hasta que nos envíes la tuya."
                />
              </div>
            )}

            {paso === 3 && (
              <div className="space-y-4">
                {estado?.requires_code && (
                  <div className="p-4 rounded-xl bg-teal-50 border border-teal-200">
                    <Label htmlFor="access_code" className="flex items-center gap-2 text-teal-900">
                      <KeyRound className="w-4 h-4" />
                      Código de invitación *
                    </Label>
                    <Input
                      id="access_code"
                      value={datos.access_code}
                      onChange={(e) => cambiar('access_code', e.target.value)}
                      placeholder="El código que te enviamos"
                      className="mt-2"
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="password">Contraseña *</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      type={verClave ? 'text' : 'password'}
                      value={datos.password}
                      onChange={(e) => cambiar('password', e.target.value)}
                      placeholder="Mínimo 8 caracteres"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setVerClave((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                      aria-label={verClave ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {verClave ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Con esta clave entrarás cuando aprobemos tu postulación.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 text-sm text-slate-600 space-y-2">
                  <p className="font-medium text-slate-800">Antes de enviar, ten presente:</p>
                  <p>
                    Los pacientes se identifican solo con un seudónimo. No vas a conocer su
                    nombre, correo ni teléfono, y no debes pedírselos.
                  </p>
                  <p>
                    La plataforma no guarda fichas clínicas ni notas de sesión: ese registro
                    es tuyo y debe vivir donde corresponda.
                  </p>
                  <p>
                    Verificamos tu número de registro antes de aprobarte.
                  </p>
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={aceptaCondiciones}
                    onCheckedChange={(v) => setAceptaCondiciones(v === true)}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-slate-700">
                    Declaro que la información es verdadera y acepto los{' '}
                    <Link to="/legales/terminos" className="text-teal-700 underline" target="_blank">
                      términos del servicio
                    </Link>
                    .
                  </span>
                </label>
              </div>
            )}

            <div className="flex gap-3 mt-8">
              {paso > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPaso((p) => p - 1)}
                  className="rounded-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Atrás
                </Button>
              )}

              {paso < 3 ? (
                <Button
                  type="button"
                  onClick={siguiente}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white rounded-full py-6"
                >
                  Continuar
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={enviando}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white rounded-full py-6"
                  data-testid="enviar-postulacion"
                >
                  {enviando ? 'Enviando…' : 'Enviar postulación'}
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <p className="text-center text-sm text-slate-500 mt-6">
        ¿Ya tienes cuenta?{' '}
        <Link to="/profesional/login" className="text-teal-700 underline">
          Entra aquí
        </Link>
      </p>
    </Marco>
  );
};

const Marco = ({ children }) => (
  <div className="min-h-screen bg-slate-50 py-12 px-4">
    <div className="max-w-2xl mx-auto">{children}</div>
  </div>
);

const Campo = ({ id, etiqueta, valor, onChange, placeholder, ayuda, tipo = 'text' }) => (
  <div>
    <Label htmlFor={id}>{etiqueta}</Label>
    <Input
      id={id}
      type={tipo}
      value={valor}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1"
    />
    {ayuda && <p className="text-xs text-slate-500 mt-1">{ayuda}</p>}
  </div>
);

export default RegistroProfesionalPage;
